import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, Image, Input, Select, Spinner, Switch, Tag, TagCloseButton, TagLabel, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import './ManageDealerProfile.scss';
import { getStoreData, getIsImpersonating } from '@/redux/app-selectors';
import s3Utils from '../../utils/s3Utils';
import AppConfig from '@/utils/app-config.mjs';
import mime from 'mime-types';
import ControlledInput from '@/common/ControllerInput';
import { CREATE_STORE_LABELS, MAX_COMPANY_LOGO_FILE_SIZE_MB, TRY_AGAIN, UPDATED_STORE_PROFILE } from '@/utils/constants';
import { manageDealerProfileValidation } from '@/queue/ducks/validationSchema';
import { licenseDetailsNewApi, putStoreDetails } from '@/api/apis';
import StoreDetailsInterface from '@/model/store.mjs';
import { ErrorToasterNotification, SimpleToasterNotification } from '@/toaster/successNotification';
import { getEightDigitLicenseKey } from '@/explore/ffl-detail/utils.mjs';
import { AxiosError } from 'axios';
import { convertInTitleCase, formatPhoneNumber, formatPhoneNumberWithoutDuplicate, IsUUID, removeHyphen, validateRedirectUrl } from '@/utils/utils';
import { useDispatch } from 'react-redux';
import { callToGetStoreData, setStoreData } from '@/redux/app-slice';
import cookie from 'js-cookie';
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
import { analyticsEvents } from '@/utils/analyticsEvents';
//@ts-ignore
import InputMask from 'react-input-mask';
import RemoveDomainConfirmation from './remove-domain-confirmation';
import { uploadData } from 'aws-amplify/storage';
import { InfoIcon } from '@chakra-ui/icons';
import { SalesChannel, SalesChannelMapping } from '@/model/store.mjs';

interface Props{
    doRefresh: number;
    isStoreAdmin:boolean;
}

function ManageDealerProfile({doRefresh, isStoreAdmin}:Props) {
    const dispatch = useDispatch();
    const storeData = getStoreData();
    const isImpersonating = getIsImpersonating();
    const blob = new Blob();
    const [brandingAssetBlob, setBrandingAssetBlob] = useState<Blob | null>(blob);
    const [isEditProfile, setIsEditProfile] = useState<boolean>(false);
    // const [selectedLicenseType, setSelectedLicenseType] = useState<string>('');
    const [name, setName] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_name',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [email, setEmail] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_email',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [domain, setDomain] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_store_domain',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [fullLicenseKey, setFullLicenseKey] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_full_license_key',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const defaultCompanyLogo = {
        file: null,
        error: '',
        placeholder: 'Browse JPG, PNG, JPEG...',
        imgSrc: AppConfig.defaultStoreLogo
    }
    const [defaultLogoURL, setDefaultLogoURL] = useState<string>('');
    const [companyLogo, setCompanyLogo] = useState<{
        file: File | null;
        error?: string;
        placeholder: string;
        imgSrc: string;
    }>(defaultCompanyLogo);
    const [phone, setPhone] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_phone',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [storeEmail, setStoreEmail] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_store_email',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [websiteUrl, setWebsiteUrl] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_website_url',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [street, setStreet] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_street',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [city, setCity] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_city',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [state, setState] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_state',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [zipcode, setZipcode] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_zipcode',
        value: '',
        error: '',
        defaultValue: ''
    });
    const [bccEmail, setBccEmail] = useState<{ id: string; value: string; error?: string; defaultValue: string }>({
        id: 'mdp_bcc_email',
        value: '',
        error: '',
        defaultValue: ''
    });

    const [isDeleteDisabled, setIsDeleteDisabled] = useState<boolean>(false);
    const [isDeletingLogo, setIsDeletingLogo] = useState<boolean>(false);
    const [isSpinnerLoading, setIsSpinnerLoading] = useState<boolean>(true);
    const [imgSRC, setImgSRC] = useState<string>(defaultCompanyLogo.imgSrc);
    const [isEnabledMFA, setIsEnabledMFA] = useState<boolean | undefined>(false);
    const [isEnabledLive, setIsEnabledLive] = useState<boolean | undefined>(false);
    const [isMultiTenant, setIsMultiTenant] = useState<boolean | undefined>(false);
    const [isRedirectReassignUrlChecked, setIsRedirectReassignUrlChecked] = useState<boolean>(false);
    const [redirectReassignmentUrl, setRedirectReassignmentUrl] = useState<string>('');
    const [redirectUrlError, setRedirectUrlError] = useState<string>('');
    const [isFirstTimeDelayEnabled, setIsFirstTimeDelayEnabled] = useState<boolean | undefined>(false);
    const [defaultIsEnabledMFA, setDefaultIsEnabledMFA] = useState<boolean | undefined>(false);
    const [defaultIsEnabledLive, setDefaultIsEnabledLive] = useState<boolean | undefined>(false);
    const [defaultIsMultiTenant, setDefaultIsMultiTenant] = useState<boolean | undefined>(false);
    const [defaultReDirectReassignToggle, setDefaultReDirectReassignToggle] = useState<boolean | undefined>(false);
    const [defaultRedirectReassignUrl, setDefaultRedirectReassignUrl] = useState<string>('');
    const [storeDomainValues, setStoreDomainValues] = useState<string[]>([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isCFD,setIsCFD] = useState<boolean | undefined>(false);
    const [removedDomainVal, setRemovedDomainVal] = useState<string>('');
    
    // Sales channel mapping state
    const [availableSalesChannels, setAvailableSalesChannels] = useState<SalesChannel[]>([]);
    const [salesChannelMappings, setSalesChannelMappings] = useState<SalesChannelMapping[]>([]);
    const [defaultSalesChannelMappings, setDefaultSalesChannelMappings] = useState<SalesChannelMapping[]>([]);
    const [selectedSalesChannel, setSelectedSalesChannel] = useState<string>('');
    const [sellerIdInput, setSellerIdInput] = useState<string>('');
    const [sellerIdError, setSellerIdError] = useState<string>('');

    useEffect(() => {
        if(doRefresh === 0){
            dispatch(callToGetStoreData())
        }
    },[doRefresh])
    useEffect(() => {
        if (storeData) {
            if (storeData?.name) {
                const contactName = storeData.name;
                setName({
                    ...name,
                    value: contactName,
                    defaultValue: contactName,
                });
            }
            if (storeData?.contact_phone) {
                const contactPhone = storeData.contact_phone;
                setPhone({
                    ...name,
                    value: contactPhone,
                    defaultValue: contactPhone,
                });
            }
            if (storeData?.website) {
                const website = storeData.website;
                setWebsiteUrl({
                    ...name,
                    value: website,
                    defaultValue: website,
                });
            }
            if (storeData?.store_premise_street) {
                const store_premise_street = storeData.store_premise_street;
                setStreet({
                    ...street,
                    value: store_premise_street,
                    defaultValue: store_premise_street,
                });
            }
            if (storeData?.store_premise_city) {
                const store_premise_city = storeData.store_premise_city;
                setCity({
                    ...city,
                    value: store_premise_city,
                    defaultValue: store_premise_city,
                });
            }
            if (storeData?.store_premise_state) {
                const store_premise_state = storeData.store_premise_state;
                setState({
                    ...state,
                    value: store_premise_state,
                    defaultValue: store_premise_state,
                });
            }
            if (storeData?.store_premise_zip_code) {
                const store_premise_zip_code = storeData.store_premise_zip_code;
                setZipcode({
                    ...zipcode,
                    value: store_premise_zip_code,
                    defaultValue: store_premise_zip_code,
                });
            }
            if (storeData?.seller_email) {
                const contactEmail = storeData.seller_email.split('@');
                setEmail({
                    ...email,
                    value: contactEmail[0],
                    defaultValue: contactEmail[0],
                });
            }
            if (storeData?.bcc_email) {
                const bcc_email = storeData.bcc_email;
                setBccEmail({
                    ...bccEmail,
                    value: bcc_email,
                    defaultValue: bcc_email,
                });
            }
            if (storeData?.contact_email) {
                const contact_email = storeData.contact_email;
                setStoreEmail({
                    ...storeEmail,
                    value: contact_email,
                    defaultValue: contact_email,
                });
            }
            if (storeData?.store_domain) {
                const storeDomain = storeData.store_domains;
                if (storeDomain && storeDomain.length > 0) {
                    setStoreDomainValues(storeDomain);
                    setDomain({
                        ...domain,
                        value: storeDomain[0],
                        defaultValue: storeDomain[0],
                    });
                }
            }
            if (storeData?.store_license_key) {
                const licenseKey = storeData?.store_license_key;
                licenseDetailsNewApi(
                    licenseKey
                ).then((data: any) => {
                    const licenseKeyValue = data?.licenses?.find((license:any) => license?.licenseKey === licenseKey)?.fullLicenseKey || ''
                    setFullLicenseKey({
                        ...fullLicenseKey,
                        value: licenseKeyValue,
                        defaultValue: licenseKeyValue
                    });
                }).catch(err => {
                    setFullLicenseKey({
                        ...fullLicenseKey,
                        error: ''
                    });
                })
            }
            if (storeData?.branding_asset_path) {
                setIsSpinnerLoading(true);
                getBrandingLogo();
            } else {
                setCompanyLogo({
                    ...companyLogo,
                    imgSrc: AppConfig.defaultStoreLogo
                });
                setIsDeleteDisabled(true);
                setIsSpinnerLoading(false);
                setImgSRC(defaultCompanyLogo.imgSrc)
            }
            setDefaultIsEnabledMFA(storeData?.is_mfa_enabled);
            setIsEnabledMFA(storeData?.is_mfa_enabled);
            setIsEnabledLive(storeData?.is_external);
            setIsMultiTenant(storeData?.is_multi_tenant);
            setIsFirstTimeDelayEnabled(storeData?.is_first_time_delay_enabled);
            setIsCFD(storeData?.is_cfd_workflow)
            setDefaultIsEnabledLive(storeData?.is_external);
            setDefaultIsMultiTenant(storeData?.is_multi_tenant)
            setIsRedirectReassignUrlChecked(!!storeData?.redirect_reassign_url);
            setRedirectReassignmentUrl(storeData?.redirect_reassign_url || '');
            setDefaultRedirectReassignUrl(storeData?.redirect_reassign_url || '');
            setDefaultReDirectReassignToggle(!!storeData?.redirect_reassign_url)
            
            // Load sales channel mappings
            if (storeData?.sales_channel_mappings && storeData.sales_channel_mappings.length > 0) {
                const mappings = storeData.sales_channel_mappings.map((m: any) => ({
                    sales_channel: m.sales_channel,
                    seller_ids: m.seller_ids || []
                }));
                setSalesChannelMappings(mappings);
                setDefaultSalesChannelMappings(mappings);
            }
        }
    }, [JSON.stringify(storeData)]);
    
    // Load available sales channels from store data
    useEffect(() => {
        if (storeData?.available_sales_channels) {
            setAvailableSalesChannels(storeData.available_sales_channels);
        }
    }, [storeData?.available_sales_channels]);

    useEffect(() => {
        if (brandingAssetBlob) {
            blobToDataURL(brandingAssetBlob).then((url) => {
                setDefaultLogoURL(url);
                setCompanyLogo({
                    ...companyLogo,
                    imgSrc: url
                });
            });
        } else {
            setCompanyLogo({
                ...companyLogo,
                imgSrc: ''
            });
        }
    }, [brandingAssetBlob]);

    const isGlobalAdminImpersonating = () => {
        return cookie.get(AppConfig.cookieStoreIdName) && isImpersonating;
    };

    const getFileContentType = (path: string) => {
        return mime.lookup(path.split('.').pop() as string) || '';
    }
    const getBrandingLogo = async () => {
        if (storeData?.branding_asset_path) {
            let blob = await s3Utils.getFileBlob(storeData?.branding_asset_path, "preferences", {
                public: ''
            });
            blob = new Blob([blob], { type: getFileContentType(storeData?.branding_asset_path) });
            setBrandingAssetBlob(blob);
            setIsDeleteDisabled(false);
        }
    }
    const blobToDataURL = (blob: Blob): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };
    async function validation(
        value: string,
        fieldName: string
    ) {
        try {
            await manageDealerProfileValidation.validateAt(fieldName, { [fieldName]: value });
            return '';
        } catch (error: any) {
            // console.error("SHEMA ERR : ", error);
            return error.errors && error.errors.length > 0 ? error.errors[0] : '';
        }
    }
    const onEditToggle = () => {
        logAnalyticsEvent(analyticsEvents.editStoreProfile);
        setIsEditProfile(!isEditProfile);
        setDomain({
            ...domain,
            value: '',
            error: '',
            defaultValue: '',
        });
    }
    const onSaveBtnClick = async () => {
        try {
            setIsLoading(true);
            let updatedDomainValues:any[] = storeDomainValues;
            if (domain.value && !storeDomainValues.includes(domain.value)) {
                updatedDomainValues = [...storeDomainValues, domain.value];
                setStoreDomainValues(updatedDomainValues);
            }
            const uniqueId = Math.floor(10000000 + Math.random() * 90000000).toString();
            let assetPath = `public/preferences/${storeData.store_id}/brandingAssets/${uniqueId}/store_logo`;
            if (companyLogo.file) {
                assetPath += `${getFileExtension(companyLogo.file.name)}`;
                console.log("PATH : ", `${AppConfig.preferencesBucket}/${assetPath}`);
                await uploadCompanyLogo(assetPath);
            } else {
                assetPath = '';
            }
        
           const domains = updatedDomainValues && updatedDomainValues.length > 0 ? updatedDomainValues.join(',') : '';
           if(domains === ''){
            setDomain({
                ...domain,
                error: 'Store domain is required'
            });
            setIsLoading(false);
            return;
           }
           if (isMultiTenant && isRedirectReassignUrlChecked && !redirectReassignmentUrl) {
                setRedirectUrlError('Enter URL or disable the toggle');
                setIsLoading(false);
                return;
            }
            const data: StoreDetailsInterface = {
                ...storeData,
                name:name.value,
                store_domain: domains,
                seller_email: email.value,
                contact_phone: phone.value ? removeHyphen(phone.value) : '',
                website: websiteUrl.value,
                store_premise_street: street.value,
                store_premise_city: city.value,
                store_premise_state: state.value,
                store_premise_zip_code: zipcode.value,
                contact_email: storeEmail.value,
                bcc_email: bccEmail.value,
                is_cfd_workflow: isCFD,
                branding_asset_path: assetPath ? `${assetPath}` : (!isDeletingLogo ? storeData?.branding_asset_path : ''),
                store_license_key: getEightDigitLicenseKey(fullLicenseKey.value),
                ...(isGlobalAdminImpersonating() || isStoreAdmin ? {is_mfa_enabled: isEnabledMFA} : {}),
                ...(isGlobalAdminImpersonating() || isStoreAdmin ? {is_external: isEnabledLive} : {}),
                ...(isGlobalAdminImpersonating() || isStoreAdmin ? {is_multi_tenant: isMultiTenant} : {}),
                ...(isGlobalAdminImpersonating() || isStoreAdmin ? {is_first_time_delay_enabled: isFirstTimeDelayEnabled} : {}),
                ...(isGlobalAdminImpersonating() || isStoreAdmin ? {redirect_reassign_url: redirectReassignmentUrl} : {}),
                // Sales channel mappings
                sales_channel_mappings: salesChannelMappings,
            }
            if(!isGlobalAdminImpersonating() && !isStoreAdmin){
                delete data.is_mfa_enabled;
            }
            delete data.store_domains;
            const storeRes = await putStoreDetails(data);
            setIsLoading(false);
            setIsDeletingLogo(false);
            if (storeRes.status === 200) {
                SimpleToasterNotification(
                    'success',
                    UPDATED_STORE_PROFILE
                );
                const licenseKey = storeRes.data[1][0]?.store_license_key
                licenseDetailsNewApi(
                    licenseKey
                ).then((data: any) => {
                    const licenseKeyValue = data?.licenses?.find((license:any) => license?.licenseKey === licenseKey)?.fullLicenseKey || ''
                    setFullLicenseKey({
                        ...fullLicenseKey,
                        value: licenseKeyValue,
                        defaultValue: licenseKeyValue
                    });
                }).catch(err => {
                    setFullLicenseKey({
                        ...fullLicenseKey,
                        error: ''
                    });
                })
                dispatch(callToGetStoreData())
                if(isGlobalAdminImpersonating() || isStoreAdmin) {
                    dispatch(setStoreData({
                        ...storeData,
                        is_mfa_enabled: isEnabledMFA,
                        is_external: isEnabledLive,
                        is_multi_tenant: isMultiTenant,
                        redirect_reassign_url: redirectReassignmentUrl,
                        dealer_id:storeRes.data[1][0]?.dealer_id
                    }))
                }
                setDefaultIsEnabledMFA(isEnabledMFA);
                setDefaultIsEnabledLive(isEnabledLive);
                setDefaultIsMultiTenant(isMultiTenant)
                setDefaultReDirectReassignToggle(!!redirectReassignmentUrl)
                setDefaultRedirectReassignUrl(redirectReassignmentUrl)
                onCancelBtnClick(true);
            }
            return;
        } catch (error) {
            console.error("ERR : ", error)
            let errorMessage = TRY_AGAIN
            if(error instanceof AxiosError) {
                if(error?.response?.data?.Error) {
                    errorMessage = error?.response?.data?.Error
                }
            }
            ErrorToasterNotification(errorMessage);
            setIsLoading(false);
            setIsDeletingLogo(false);
            return;
        }
    }
    const onChangeName = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let nameData = {
            ...name,
            value:value,
            error:''
        }
        if(error){
            nameData['error'] = error;
        }
        setName(nameData)
    }
    const onChangeStoreEmail = async (value: string, fieldName: string) => {
        let error = await validation(value, fieldName);
        const cleanValue = value.replace(/\s+/g, '');
        let storeEmailData = {
            ...storeEmail,
            value: cleanValue,
            error: ''
        }
        if (error) {
            storeEmailData['error'] = error;
        }
        setStoreEmail(storeEmailData);
    }
    const onChangeBccEmail = async (value: string, fieldName: string) => {
        let error = await validation(value, fieldName);
        const cleanValue = value.replace(/\s+/g, '');
        let bccEmailData = {
            ...bccEmail,
            value: cleanValue,
            error: ''
        }
        if (error) {
            bccEmailData['error'] = error;
        }
        setBccEmail(bccEmailData);
    }
    const onChangePhone = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let phoneData = {
            ...phone,
            value:value,
            error:''
        }
        if(error){
            phoneData['error'] = error;
        }
        setPhone(phoneData)
    }
    const onChangeWebsiteUrl = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        const cleanValue = value.replace(/\s+/g, '');
        let websiteUrlData = {
            ...websiteUrl,
            value:cleanValue,
            error:''
        }
        if(error){
            websiteUrlData['error'] = error;
        }
        setWebsiteUrl(websiteUrlData)
    }
    const onChangeStreet = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let streetData = {
            ...street,
            value:value,
            error:''
        }
        if(error){
            streetData['error'] = error;
        }
        setStreet(streetData)
    }
    const onChangeCity = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let cityData = {
            ...city,
            value:value,
            error:''
        }
        if(error){
            cityData['error'] = error;
        }
        setCity(cityData)
    }
    const onChangeState = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let stateData = {
            ...state,
            value:value,
            error:''
        }
        if(error){
            stateData['error'] = error;
        }
        setState(stateData)
    }
    const onChangeZipcode = async (value: string, fieldName: string) => {
        let error =  await validation(value,fieldName);
        let zipcodeData = {
            ...zipcode,
            value:value,
            error:''
        }
        if(error){
            zipcodeData['error'] = error;
        }
        setZipcode(zipcodeData)
    }
    const onChangeEmail = async (value: string, fieldName: string) => {
        let error = await validation(value, fieldName);
        const cleanValue = value.replace(/\s+/g, '');
        let emailData = {
            ...email,
            value: cleanValue,
            error: ''
        }
        if (error) {
            emailData['error'] = error;
        }
        setEmail(emailData);
    }
    const onChangeDomain = async (value: string, fieldName: string) => {
        const error = await validation(value, fieldName);
        const cleanValue = value.replace(/\s+/g, '');
         // Check for duplicate domain
        const isDuplicate = cleanValue && storeDomainValues.includes(cleanValue);
    
        const domainData = {
            ...domain,
            value: cleanValue,
            error: ''
        }
        if (error) {
            domainData['error'] = error;
        }else if (isDuplicate) {
            domainData['error'] = 'This domain already exists';
        }
        setDomain(domainData);
    }

    const onChangeLicenseNumber = async (value: string, fieldName: string) => {

        let formattedValue = value;

        let error = await validation(formattedValue, fieldName);
        let LicenseData = {
            ...fullLicenseKey,
            value: formattedValue,
            error: ''
        }
        if (error) {
            LicenseData['error'] = error;
        }
        setFullLicenseKey(LicenseData);
    }
    // const onChangeLicenseType = (value: string) => {
    //     setSelectedLicenseType(value);
    // }
    const onChangeLogo = (event: any) => {
        if (event.target.files && event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            if (selectedFile.size > MAX_COMPANY_LOGO_FILE_SIZE_MB * 1024 * 1024) {
                setCompanyLogo({
                    ...defaultCompanyLogo,
                    error: `*Maximum ${MAX_COMPANY_LOGO_FILE_SIZE_MB} MB file size.*`
                })
                return;
            }
            if (selectedFile) {
                setIsDeleteDisabled(false);
                blobToDataURL(selectedFile).then((url) => {
                    setCompanyLogo({
                        ...companyLogo,
                        file: selectedFile,
                        placeholder: selectedFile.name,
                        imgSrc: url,
                        error: ''
                    });
                    setImgSRC(url);
                });
            }
        }
    }
    const getFileExtension = (filename: string) => {
        return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 1).toLowerCase();
    };
    async function uploadCompanyLogo(assetPath: string) {
        if (companyLogo.file) {
            await uploadData({
              path: assetPath,
              data: companyLogo.file,
              options: {
                bucket: "preferences",
                contentType: companyLogo.file.type,
              }
            });
            return;
        }
    }
    const onSelectLogoHandler = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    const onDeleteCompanyLogo = () => {
        setCompanyLogo({
            ...defaultCompanyLogo
        })
        setIsDeleteDisabled(true);
        setIsSpinnerLoading(false);
        setIsDeletingLogo(true);
        setImgSRC(defaultCompanyLogo.imgSrc);
    }
    const onCancelBtnClick = (isNew: boolean = false) => {
        if (isNew) {
            setCompanyLogo({
                ...companyLogo,
                error: '',
            })
            setFullLicenseKey({
                ...fullLicenseKey,
                error: '',
            })
            setEmail({
                ...email,
                error: '',
            });
            setStoreEmail({
                ...storeEmail,
                error: '',
            });
            setBccEmail({
                ...bccEmail,
                error: '',
            });
            setPhone({
                ...phone,
                error: '',
            });
            setWebsiteUrl({
                ...websiteUrl,
                error: '',
            });
            setStreet({
                ...street,
                error: '',
            });
            setCity({
                ...city,
                error: '',
            });
            setState({
                ...state,
                error: '',
            });
            setZipcode({
                ...zipcode,
                error: '',
            });
            setDomain({
                ...domain,
                error: '',
            });
            setName({
                ...name,
                error:''
            })
            setRedirectUrlError('');
        } else {
            setCompanyLogo({
                ...defaultCompanyLogo,
                imgSrc: defaultLogoURL,
                error: '',
            })
            setFullLicenseKey({
                ...fullLicenseKey,
                value: fullLicenseKey.defaultValue,
                error: '',
            })
            setEmail({
                ...email,
                value: email.defaultValue,
                error: '',
            });
            setStoreEmail({
                ...storeEmail,
                value: storeEmail.defaultValue,
                error: '',
            });
            setBccEmail({
                ...bccEmail,
                value: bccEmail.defaultValue,
                error: '',
            });
            setPhone({
                ...phone,
                value: phone.defaultValue,
                error: '',
            });
            setWebsiteUrl({
                ...websiteUrl,
                value: websiteUrl.defaultValue,
                error: '',
            });
            setStreet({
                ...street,
                value: street.defaultValue,
                error: '',
            });
            setCity({
                ...city,
                value: city.defaultValue,
                error: '',
            });
            setState({
                ...state,
                value: state.defaultValue,
                error: '',
            });
            setZipcode({
                ...zipcode,
                value: zipcode.defaultValue,
                error: '',
            });
            setDomain({
                ...domain,
                value: domain.defaultValue,
                error: '',
            });
            setName({
                ...name,
                value: name.defaultValue,
                error: '',
            })
            setIsEnabledMFA(defaultIsEnabledMFA)
            setIsEnabledLive(defaultIsEnabledLive)
            setIsMultiTenant(defaultIsMultiTenant)
            setRedirectReassignmentUrl(defaultRedirectReassignUrl)
            setIsRedirectReassignUrlChecked(!!defaultReDirectReassignToggle)
            setRedirectUrlError('');
            // Reset sales channel values
            setSalesChannelMappings([...defaultSalesChannelMappings]);
            setSelectedSalesChannel('');
            setSellerIdInput('');
            setSellerIdError('');
        }
        onEditToggle();
        const storeDomain = storeData.store_domains;
                if (storeDomain && storeDomain.length > 0) {
                    setStoreDomainValues(storeDomain);
                    setDomain({
                        ...domain,
                        value: storeDomain[0],
                        defaultValue: storeDomain[0],
                    });
                }
    }
    const onChangeMFAEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsEnabledMFA(event.target.checked);
    }
    const onChangeLiveEnabled = (event : React.ChangeEvent<HTMLInputElement>) => {
        setIsEnabledLive(event.target.checked);
    }
    const onChangeIsMultiTenant = (event : React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            setIsRedirectReassignUrlChecked(false);
            setRedirectReassignmentUrl('');
            setRedirectUrlError('');
        }
        setIsMultiTenant(event.target.checked);
    }
    const onChangeFirstTimeDelayEnabled = (event : React.ChangeEvent<HTMLInputElement>) => {
        setIsFirstTimeDelayEnabled(event.target.checked);
    }
    const onChangeCFD = (event : React.ChangeEvent<HTMLInputElement>) => {
        setIsCFD(event.target.checked);
    }
    const onChangeRedirectReassignUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsRedirectReassignUrlChecked(event.target.checked);
        if (!event.target.checked) {
            setRedirectReassignmentUrl('');
            setRedirectUrlError('');
        }
    }
    const onChangeRedirectUrl = (value: string) => {
        setRedirectReassignmentUrl(value);
        setRedirectUrlError(validateRedirectUrl(value));
    }
    const openFFLDealerDetails = () => {
        if(storeData?.dealer_id) {
            const linkUrl = `/dealers-search/${storeData.dealer_id}`;
            window.open(linkUrl,'_blank');
        }
    }
    const onClickAddMoreDomain = () => {
        if (storeDomainValues.length >= 20) {
            setDomain({ ...domain, value: domain.value, error: 'Maximum 20 domains allowed' });
            return;
        }
            setStoreDomainValues([...storeDomainValues, domain.value]);
            setDomain({ ...domain, value: '', error: '' });
        
    }
    const handleRemoveDoaminTag = (value: string) => {
        setRemovedDomainVal(value);
        const storeDomain = storeData.store_domains;
        
        if (storeDomain && storeDomain.length > 0) {
            // Check if the domain exists in storeDomain
            const domainExists = storeDomain.includes(value);
            
            if (domainExists) {
                // Domain found in storeDomain, show modal
                onOpen();
            } else {
                // Domain not found in storeDomain, bypass modal
                // Directly remove the domain without showing modal
                const newStores = storeDomainValues.filter((domain: string) => domain !== value);
                // You might want to update your state here directly
                setStoreDomainValues(newStores); // Uncomment if you have this setter
            }
        }
    }
    const onRemoveDoaminTag = () => {
        if(removedDomainVal){
            const newStores = storeDomainValues.filter((domain: string) => domain !== removedDomainVal);
            setStoreDomainValues([...newStores]);
            onClose();
            setRemovedDomainVal('');
        }
    }
    
    // Sales channel handlers
    const onClickAddMoreSellerId = () => {
        if (!selectedSalesChannel) {
            setSellerIdError('Please select a sales channel first');
            return;
        }
        if (!sellerIdInput.trim()) {
            setSellerIdError('Please enter a seller username');
            return;
        }
        
        // Find existing mapping for this sales channel
        const existingMapping = salesChannelMappings.find(m => m.sales_channel === selectedSalesChannel);
        const currentSellerIds = existingMapping?.seller_ids || [];
        
        if (currentSellerIds.length >= 20) {
            setSellerIdError('Maximum 20 usernames allowed per sales channel');
            return;
        }
        
        // Check for duplicates (case-insensitive)
        if (currentSellerIds.some(id => id.toLowerCase() === sellerIdInput.trim().toLowerCase())) {
            setSellerIdError('This username is already added');
            return;
        }
        
        // Update or create the mapping
        if (existingMapping) {
            setSalesChannelMappings(salesChannelMappings.map(m => 
                m.sales_channel === selectedSalesChannel 
                    ? { ...m, seller_ids: [...m.seller_ids, sellerIdInput.trim()] }
                    : m
            ));
        } else {
            setSalesChannelMappings([...salesChannelMappings, {
                sales_channel: selectedSalesChannel,
                seller_ids: [sellerIdInput.trim()]
            }]);
        }
        setSellerIdInput('');
        setSellerIdError('');
    }
    
    const handleRemoveSellerIdTag = (salesChannel: string, sellerId: string) => {
        setSalesChannelMappings(salesChannelMappings.map(m => {
            if (m.sales_channel === salesChannel) {
                const newSellerIds = m.seller_ids.filter(id => id !== sellerId);
                return { ...m, seller_ids: newSellerIds };
            }
            return m;
        }).filter(m => m.seller_ids.length > 0)); // Remove mapping if no seller IDs left
    }
    
    const onChangeSalesChannel = (value: string) => {
        setSelectedSalesChannel(value);
        setSellerIdError('');
    }
    
    const onChangeSellerIdInput = (value: string) => {
        setSellerIdInput(value);
        setSellerIdError('');
    }
    
    const getSalesChannelName = (storeId: string) => {
        // First check if the mapping has the name (from backend enrichment)
        const mapping = salesChannelMappings.find(m => m.sales_channel === storeId);
        if (mapping?.sales_channel_name) {
            return mapping.sales_channel_name;
        }
        // Fallback to available channels list
        const channel = availableSalesChannels.find(ch => ch.store_id === storeId);
        return channel?.name || storeId;
    }
    
    const getSellerIdsForSelectedChannel = () => {
        const mapping = salesChannelMappings.find(m => m.sales_channel === selectedSalesChannel);
        return mapping?.seller_ids || [];
    }
    
    return (
        <Box>
            <RemoveDomainConfirmation isOpen={isOpen} onClose={onClose} onSaveClick={onRemoveDoaminTag} />
            <form className="manage-dealer-profile-form">
                <ul className="manage-dealer-profile">
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Sender Email:</span>
                        <span className="manage-dealer-profile-right-value">
                            {isEditProfile ?
                                <>
                                    <div className='mdp-email-input-wrapper'>
                                        <div className='mdp-email-input'>
                                            <ControlledInput
                                                className="manage-dealer-profile__input manage-dealer-profile-form__dealer-email"
                                                type="text"
                                                placeholder="Enter email"
                                                value={email.value}
                                                onChange={(event) => onChangeEmail(event.target.value, 'email')}
                                            />
                                            <Text>
                                                @masterffl.com
                                            </Text>
                                        </div>
                                        {email.error && (
                                            <div className="edit-error">* {email.error}</div>
                                        )}
                                    </div>
                                </> : <Text> {`${email.value}@masterffl.com` || '-'} </Text>
                            }
                        </span>
                    </li>
                    {/* <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Store ID:</span>
                        <span className="manage-dealer-profile-right-value">
                            <Text>
                                {storeData?.store_id || '-'}
                            </Text>
                        </span>
                    </li> */}
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Store Domain:</span>
                        <span className="manage-dealer-profile-right-value">
                            {isEditProfile ?
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <span style={{ width: '100%', maxWidth: '408px' }}>
                                        <ControlledInput
                                            className="manage-dealer-profile__input"
                                            type="text"
                                            placeholder="example.com"
                                            value={IsUUID(domain.value) ? '' : domain.value}
                                            onChange={(event) => onChangeDomain(event.target.value, 'domain')}
                                        />
                                        {domain.error && (
                                            <div className="edit-error">* {domain.error}</div>
                                        )}
                                    </span>

                                    <button
                                        className="add-more-text more-domain"
                                        disabled={domain?.error !== '' || !domain.value || IsUUID(domain.value)}
                                        onClick={onClickAddMoreDomain}
                                    >
                                        +Add More
                                    </button>
                                    <Box className='left-inner-clips-box' sx={{ marginTop: '0.5rem' }}>
                                        {storeDomainValues && storeDomainValues.length > 0 && storeDomainValues.map((domainVal: string, index: number) => (
                                            <Tag className='chip-box' key={`domain_${index}`}>
                                                <TagLabel className='tag-label'>{domainVal}</TagLabel>
                                                <TagCloseButton
                                                    padding={'0px'}
                                                    fontSize={'15px'}
                                                    color={'#436175'}
                                                    onClick={() => handleRemoveDoaminTag(domainVal)}
                                                />
                                            </Tag>
                                        ))}
                                    </Box>
                                </Box> :
                                <>
                                    <span className="manage-dealer-profile-right-value">
                                        <Text>
                                            {domain.value && IsUUID(domain.value) ? '-' : domain.value || "-"}
                                        </Text>
                                        {storeDomainValues && storeDomainValues.length > 1 && (
                                            <Tooltip
                                                label={
                                                    <Box>
                                                        <span>
                                                            {storeDomainValues.slice(1).map((domainVal: string, index: number) => (
                                                                <Text key={`domain_${index}`}>{domainVal}</Text>
                                                            ))}
                                                        </span>
                                                    </Box>
                                                }
                                                hasArrow
                                                placement="bottom"
                                                color={'black'}
                                                bg={'white'}
                                            >
                                                <Text sx={{ marginLeft: '0.5rem' }}>
                                                    +{storeDomainValues.length - 1}
                                                </Text>
                                            </Tooltip>
                                        )}
                                    </span>
                                </>
                            }
                        </span>
                    </li>
                    
                    {/* Sales Channel Mapping */}
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">
                            Sales Channel:
                        </span>
                        <span className="manage-dealer-profile-right-value">
                            {isEditProfile ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    {/* Sales Channel Dropdown */}
                                    <Select
                                        className="manage-dealer-profile__input"
                                        style={{ width: '100%', maxWidth: '408px', marginBottom: '8px' }}
                                        placeholder="Select Sales Channel"
                                        value={selectedSalesChannel}
                                        onChange={(e) => onChangeSalesChannel(e.target.value)}
                                        isDisabled={availableSalesChannels.length === 0}
                                    >
                                        {availableSalesChannels.length === 0 ? (
                                            <option value="" disabled>No sales channels available</option>
                                        ) : (
                                            availableSalesChannels.map((channel) => (
                                                <option key={channel.store_id} value={channel.store_id}>
                                                    {channel.name}
                                                </option>
                                            ))
                                        )}
                                    </Select>
                                        
                                        {/* Seller Username Input */}
                                        <span style={{ width: '100%', maxWidth: '408px' }}>
                                            <ControlledInput
                                                className="manage-dealer-profile__input"
                                                type="text"
                                                placeholder="Enter seller username"
                                                value={sellerIdInput}
                                                onChange={(event) => onChangeSellerIdInput(event.target.value)}
                                                onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && (e.preventDefault(), onClickAddMoreSellerId())}
                                            />
                                            {sellerIdError && (
                                                <div className="edit-error">* {sellerIdError}</div>
                                            )}
                                        </span>
                                        
                                        <button
                                            className="add-more-text more-domain"
                                            disabled={!selectedSalesChannel || !sellerIdInput.trim()}
                                            onClick={onClickAddMoreSellerId}
                                            type="button"
                                        >
                                            +Add More
                                        </button>
                                        
                                        {/* Display ALL mappings grouped by sales channel */}
                                        {salesChannelMappings.length > 0 && (
                                            <Box sx={{ marginTop: '1rem' }}>
                                                {salesChannelMappings.map((mapping, mappingIndex) => (
                                                    <Box key={`mapping_${mappingIndex}`} sx={{ marginBottom: '0.75rem' }}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={1}>
                                                            {getSalesChannelName(mapping.sales_channel)}:
                                                        </Text>
                                                        <Box className='left-inner-clips-box'>
                                                            {mapping.seller_ids.map((sellerId: string, sellerIndex: number) => (
                                                                <Tag className='chip-box' key={`seller_${mappingIndex}_${sellerIndex}`}>
                                                                    <TagLabel className='tag-label'>{sellerId}</TagLabel>
                                                                    <TagCloseButton
                                                                        padding={'0px'}
                                                                        fontSize={'15px'}
                                                                        color={'#436175'}
                                                                        onClick={() => handleRemoveSellerIdTag(mapping.sales_channel, sellerId)}
                                                                    />
                                                                </Tag>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <span className="manage-dealer-profile-right-value" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        {salesChannelMappings.length > 0 ? (
                                            salesChannelMappings.map((mapping, index) => (
                                                <Box key={`view_mapping_${index}`} sx={{ marginBottom: index < salesChannelMappings.length - 1 ? '0.5rem' : 0 }}>
                                                    <Text fontWeight="medium">{getSalesChannelName(mapping.sales_channel)}</Text>
                                                    <Tooltip
                                                        label={
                                                            <Box>
                                                                <Text fontWeight="bold" mb={1}>Usernames:</Text>
                                                                {mapping.seller_ids.map((sellerId: string, idx: number) => (
                                                                    <Text key={`tooltip_${index}_${idx}`}>{sellerId}</Text>
                                                                ))}
                                                            </Box>
                                                        }
                                                        hasArrow
                                                        placement="bottom"
                                                        color={'black'}
                                                        bg={'white'}
                                                    >
                                                        <Text sx={{ color: 'blue.500', cursor: 'pointer', fontSize: 'sm' }}>
                                                            ({mapping.seller_ids.length} username{mapping.seller_ids.length > 1 ? 's' : ''})
                                                        </Text>
                                                    </Tooltip>
                                                </Box>
                                            ))
                                        ) : (
                                            <Text>-</Text>
                                        )}
                                    </span>
                                )}
                        </span>
                    </li>
                    
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Store Logo:</span>
                        <div className="manage-dealer-profile-right-value" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <>
                                <div style={{ display: 'flex', width: '100%', alignItems: 'center', maxWidth: '408px' }}>
                                    <div style={{ flexGrow: 1 }}>
                                        {companyLogo.imgSrc && brandingAssetBlob?.size != 0 ?
                                            <Image className='s-company-logo' src={companyLogo.imgSrc} />
                                            : (isSpinnerLoading ? <Spinner width={'20px'} height={'20px'} /> : <Image className='s-company-logo' src={imgSRC} />)
                                        }
                                    </div>
                                    {isEditProfile ?
                                        <span className='mdp-no-shrink'>
                                            <Button
                                                className="mdp-license__delete"
                                                onClick={() => onDeleteCompanyLogo()}
                                                isDisabled={isDeleteDisabled}
                                            >
                                                <Image src="/images/ui/delete-red.svg" />
                                                <Text style={{ color: "red", marginLeft: "5px" }}>Delete</Text>
                                            </Button>
                                        </span> : ''
                                    }
                                </div>
                                {isEditProfile ?
                                    <div style={{ marginTop: '20px', width: '100%', maxWidth: '408px' }}>
                                        <>
                                            <Box className='mdp-upload-input'>
                                                <Input
                                                    pr='4.5rem'
                                                    type='file'
                                                    ref={fileInputRef}
                                                    onChange={(e) => onChangeLogo(e)}
                                                    display={'none'}
                                                    accept='.png,.jpg,.jpeg,.gif'
                                                    isDisabled={isLoading}
                                                />
                                                <Text sx={{ padding: '5px 10px', flexGrow: 1 }}
                                                    onClick={onSelectLogoHandler}>
                                                    <span className='mdp-placeholder'>
                                                        {companyLogo.placeholder}
                                                    </span>
                                                </Text>
                                                <Button
                                                    onClick={onSelectLogoHandler}
                                                    background="#D7EBF9"
                                                    padding="8px 20px"
                                                    isDisabled={isLoading}
                                                    className='mdp-upload-btn'
                                                >
                                                    <Text style={{ color: '#0C1E46' }}></Text> Upload
                                                </Button>
                                            </Box>
                                            {companyLogo.error && (
                                                <div className="edit-error">{companyLogo.error}</div>
                                            )}
                                        </>
                                    </div> : ''
                                }
                            </>
                        </div>
                    </li>
                    {/* <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Is MFA Enabled:</span>
                        {isEditProfile ? (
                            <span className="manage-dealer-profile-right-value">
                                <Switch
                                    size="md"
                                    isChecked={isEnabledMFA}
                                    onChange={onChangeMFAEnabled}
                                    disabled={!isEditProfile}
                                />
                            </span>
                        ) : (<span className='manage-dealer-profile-right-value'>
                            {isEnabledMFA ? <Text>Yes</Text> : <Text>No</Text>}
                        </span>)}

                    </li> */}
                    {
                        isGlobalAdminImpersonating()
                            ? <li className="manage-dealer-profile-item">
                                <span className="manage-dealer-profile-label">Is Live:</span>
                                {isEditProfile ? (
                                    <span className="manage-dealer-profile-right-value">
                                        <Switch
                                            size="md"
                                            isChecked={isEnabledLive}
                                            onChange={onChangeLiveEnabled}
                                            disabled={!isEditProfile}
                                        />
                                    </span>
                        ):(
                                    <span className='manage-dealer-profile-right-value'>
                              {isEnabledLive? <Text>Yes</Text> : <Text>No</Text>}
                                    </span>
                                )}
                    </li>: ''} 
                    {
                        isGlobalAdminImpersonating()
                            ? <li className="manage-dealer-profile-item">
                                <span className="manage-dealer-profile-label">{CREATE_STORE_LABELS.IS_MULTITENANT_LABEL}:</span>
                                {isEditProfile ? (
                                    <span className="manage-dealer-profile-right-value">
                                        <Switch
                                            size="md"
                                            isChecked={isMultiTenant}
                                            onChange={onChangeIsMultiTenant}
                                            disabled={!isEditProfile}
                                        />
                                    </span>
                        ):(
                                    <span className='manage-dealer-profile-right-value'>
                              {isMultiTenant ? <Text>Yes</Text> : <Text>No</Text>}
                                    </span>
                                )}
                    </li>: ''}
                    {
                        isGlobalAdminImpersonating() && isMultiTenant 
                            ? <li className="manage-dealer-profile-item">
                                <span className="manage-dealer-profile-label">{CREATE_STORE_LABELS.CUSTOM_REASSIGNMENT_URL_MULTI_VENDOR}
                                    <Tooltip 
                                        label={CREATE_STORE_LABELS.REASSIGNMENT_REDIRECT_URL_INFO_LABEL} 
                                        placement="top"
                                        hasArrow
                                        color="black"
                                    >
                                        <InfoIcon style={{ marginLeft: '5px', cursor: 'pointer', fontSize: '13px', top: '-2px', position: 'relative' }} />
                                    </Tooltip>:</span>
                                {isEditProfile ? (
                                    <span className="manage-dealer-profile-right-value">
                                        <Switch
                                            size="md"
                                            isChecked={isRedirectReassignUrlChecked}
                                            onChange={onChangeRedirectReassignUrl}
                                            disabled={!isEditProfile}
                                        />
                                    </span>
                        ):(
                                    <span className='manage-dealer-profile-right-value'>
                              {isRedirectReassignUrlChecked ? <Text>Yes</Text> : <Text>No</Text>}
                                    </span>
                                )}
                    </li>: ''}
                    {isGlobalAdminImpersonating() && isMultiTenant && isRedirectReassignUrlChecked ? (
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">{CREATE_STORE_LABELS.REASSIGNMENT_URL_LINK_TEXT}:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ? (
                                    <div className='mdp-email-input-wrapper'>
                                        <div className='mdp-email-input'>
                                            <ControlledInput
                                                className="manage-dealer-profile__input"
                                                type="text"
                                                placeholder="https://example.com"
                                                value={redirectReassignmentUrl}
                                                onChange={(event) => onChangeRedirectUrl(event.target.value)}
                                            />
                                        </div>
                                        {redirectUrlError && (
                                            <div className="edit-error">* {redirectUrlError}</div>
                                        )}
                                    </div>
                                ) : (
                                    <Text>{redirectReassignmentUrl || '-'}</Text>
                                )}
                            </span>
                        </li>
                    ) : ''} 
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">{CREATE_STORE_LABELS.CFD_LABEL} : </span>
                        {isEditProfile ? (
                            <span className="manage-dealer-profile-right-value">
                                <Switch
                                    size="md"
                                    isChecked={isCFD}
                                    onChange={onChangeCFD}
                                    disabled={!isEditProfile}
                                />
                            </span>
                        ): (
                            <span className='manage-dealer-profile-right-value'>
                              {isCFD? <Text>Yes</Text> : <Text>No</Text>}
                            </span>
                        )}
                    </li>
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">Is First Time Delay Enabled:</span>
                        {isEditProfile ? (
                            <span className="manage-dealer-profile-right-value">
                                <Switch
                                    size="md"
                                    isChecked={isFirstTimeDelayEnabled}
                                    onChange={onChangeFirstTimeDelayEnabled}
                                    disabled={!isEditProfile}
                                />
                            </span>
                        ): (
                            <span className='manage-dealer-profile-right-value'>
                              {isFirstTimeDelayEnabled? <Text>Yes</Text> : <Text>No</Text>}
                            </span>
                        )}
                    </li>
                    <li className="manage-dealer-profile-item">
                        <span className="manage-dealer-profile-label">BCC Email:</span>
                        <span className="manage-dealer-profile-right-value">
                            {isEditProfile ?
                                <>
                                    <div className='mdp-email-input-wrapper'>
                                        <div className='mdp-email-input'>
                                            <ControlledInput
                                                className="manage-dealer-profile__input manage-dealer-profile-form__dealer-email"
                                                type="text"
                                                placeholder="Enter bcc email"
                                                value={bccEmail.value}
                                                onChange={(event) => onChangeBccEmail(event.target.value, 'bcc_email')}
                                            />
                                        </div>
                                        {bccEmail.error && (
                                            <div className="edit-error">* {bccEmail.error}</div>
                                        )}
                                    </div>
                                </> : <Text> {bccEmail.value} </Text>
                            }
                        </span>
                    </li>
                </ul>
                <div className='email-signature'>
                    <ul>
                        <li className='manage-dealer-profile-item'>
                            <Text fontSize={'xl'} as='b'>Email Signature</Text>
                        </li>

                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">Store Name:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <div className='mdp-email-input-wrapper'>
                                            <div className='mdp-email-input'>
                                                <ControlledInput
                                                    className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                    type="text"
                                                    placeholder="Enter Name"
                                                    value={name.value}
                                                    onChange={(event) => onChangeName(event.target.value, 'name')}
                                                />
                                            </div>
                                            {name.error && (
                                                <div className="edit-error">* {name.error}</div>
                                            )}
                                        </div>
                                    </> : <Text>{name.value} </Text>
                                }
                            </span>
                        </li>
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">Store Email:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <div className='mdp-email-input-wrapper'>
                                            <div className='mdp-email-input'>
                                                <ControlledInput
                                                    className="manage-dealer-profile__input manage-dealer-profile-form__dealer-email"
                                                    type="text"
                                                    placeholder="Enter email"
                                                    value={storeEmail.value}
                                                    onChange={(event) => onChangeStoreEmail(event.target.value, 'store_email')}
                                                />
                                            </div>
                                            {storeEmail.error && (
                                                <div className="edit-error">* {storeEmail.error}</div>
                                            )}
                                        </div>
                                    </> : <Text> {storeEmail.value} </Text>
                                }
                            </span>
                        </li>
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">Phone:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <div className='mdp-email-input-wrapper'>
                                            <div className='mdp-email-input'>
                                                <ControlledInput
                                                    className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                    type="text"
                                                    placeholder="Enter Phone"
                                                    value={phone.value}
                                                    onChange={(event) => onChangePhone(event.target.value, 'phone')}
                                                />
                                            </div>
                                            {phone.error && (
                                                <div className="edit-error">* {phone.error}</div>
                                            )}
                                        </div>
                                    </> : <Text>{phone.value} </Text>
                                }
                            </span>
                        </li>
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">Website URL:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <div className='mdp-email-input-wrapper'>
                                            <div className='mdp-email-input'>
                                                <ControlledInput
                                                    className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                    type="text"
                                                    placeholder="Enter Website Url"
                                                    value={websiteUrl.value}
                                                    onChange={(event) => onChangeWebsiteUrl(event.target.value, 'website_url')}
                                                />
                                            </div>
                                            {websiteUrl.error && (
                                                <div className="edit-error">* {websiteUrl.error}</div>
                                            )}
                                        </div>
                                    </> : <Text>{websiteUrl.value} </Text>
                                }
                            </span>
                        </li>
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">Mailing Address:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <div className='mdp-address-input-wrapper'>
                                            <div className='mdp-address-input-inner-wrapper'>
                                                <div className='mdp-street-input'>
                                                    <ControlledInput
                                                        className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                        type="text"
                                                        placeholder="Enter Street"
                                                        value={street.value}
                                                        onChange={(event) => onChangeStreet(event.target.value, 'street')}
                                                    />
                                                    {street.error && (
                                                        <div className="edit-error">* {street.error}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='mdp-address-input-inner-wrapper'>
                                                <div className="mdp-city-input-wrapper">
                                                    <div className='mdp-address-input'>
                                                        <ControlledInput
                                                            className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                            type="text"
                                                            placeholder="Enter City"
                                                            value={city.value}
                                                            onChange={(event) => onChangeCity(event.target.value, 'city')}
                                                        />
                                                        {city.error && (
                                                            <div className="edit-error">* {city.error}</div>
                                                        )}
                                                    </div>
                                                    <div className='mdp-address-input'>
                                                        <ControlledInput
                                                            className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                            type="text"
                                                            placeholder="Enter State"
                                                            value={state.value}
                                                            onChange={(event) => onChangeState(event.target.value, 'state')}
                                                        />
                                                        {state.error && (
                                                            <div className="edit-error">* {state.error}</div>
                                                        )}
                                                    </div>
                                                    <div className='mdp-address-input'>
                                                        <ControlledInput
                                                            className="manage-dealer-profile__input manage-dealer-profile-form__dealer-name"
                                                            type="text"
                                                            placeholder="Enter Zipcode"
                                                            value={zipcode.value}
                                                            onChange={(event) => onChangeZipcode(event.target.value, 'zipcode')}
                                                        />
                                                        {zipcode.error && (
                                                            <div className="edit-error">* {zipcode.error}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </> : <div>
                                        <Text>{`${street.value || ''}`}</Text>
                                        <Text>{` ${city.value || ''}, ${state.value || ''} ${zipcode.value || ''}`} </Text>
                                    </div>
                                }
                            </span>
                        </li>
                        <li className="manage-dealer-profile-item">
                            <span className="manage-dealer-profile-label">FFL:</span>
                            <span className="manage-dealer-profile-right-value">
                                {isEditProfile ?
                                    <>
                                        <span style={{ width: '100%', maxWidth: '401px' }}>
                                            <InputMask
                                                id="formattedInput"
                                                mask="9-99-999-99-9a-99999"
                                                placeholder="X-XX-XXX-XX-XX-XXXXX"
                                                value={fullLicenseKey.value || ""}
                                                onChange={(event: any) =>
                                                    onChangeLicenseNumber(event.target.value, 'full_license_key')
                                                }
                                            >
                                                {(inputProps: any) => <input
                                                    {...inputProps}
                                                    type="text"
                                                    className='sot-customize-input'
                                                />
                                                }
                                            </InputMask>
                                            {fullLicenseKey.error && (
                                                <div className="edit-error">* {fullLicenseKey.error}</div>
                                            )}
                                        </span>
                                        {/* <div className="edit-license-key-preview">{fullLicenseKey.value ? `Our FFL ${fullLicenseKey.value}` : '-'}</div> */}
                                        {/* <span className='mdp-no-shrink'>
                                            <AriaSelectDropdown
                                                onChangeDropdown={(value: string) => onChangeLicenseType(value)}
                                                selectedOption={selectedLicenseType}
                                                options={Object.entries(licenseType)}
                                                className="react-aria-Select react-aria-select-light"
                                                variant="light"
                                                placeholder="-Type-"
                                            />
                                        </span> */}

                                    </> :
                                    <>
                                        <Text>
                                            {fullLicenseKey.value ?
                                                <>
                                                    <a
                                                        onClick={openFFLDealerDetails}
                                                        className="transfer-details__link"
                                                        href="#"
                                                    >
                                                        {fullLicenseKey.value}
                                                    </a>
                                                </>
                                                :
                                                <Spinner width={'20px'} height={'20px'} />
                                            }
                                        </Text>
                                        {/* {storeData && storeData?.store_lic_type && (<Text className='mdp-license-type'>
                                            {licenseType[selectedLicenseType]}
                                        </Text>)} */}
                                    </>
                                }
                            </span>
                        </li>
                    </ul>
                    <div className="email-signature-preview">
                        <p><b>Signature Preview:</b></p>
                        {name.value && <p>{`${name.value} FFL Compliance Team`}</p>}
                        {storeEmail.value && <p className='link'><u>{`${storeEmail.value}`}</u></p>}
                        {phone.value && <p>{`Phone ${formatPhoneNumberWithoutDuplicate(phone.value)}`}</p>}
                        {websiteUrl.value && <p className='link'><u>{`${websiteUrl.value}`}</u></p>}
                        <p>{`${street.value ? convertInTitleCase(street.value) : ''}`}</p>
                        <p>{` ${city.value ? convertInTitleCase(city.value) + ',' : ''} ${state.value || ''} ${zipcode.value || ''}`}</p>
                        <p className='link'><u>{fullLicenseKey.value ? `Our FFL ${fullLicenseKey.value}` : ''}</u></p>
                    </div>
                </div>
                <div className='manage-dealer-profile-btns'>
                    {isEditProfile ?
                        <>
                            <Button
                                colorScheme="blue"
                                onClick={() => { onCancelBtnClick() }}
                                background="#ffffff"
                                color="#0C1E46"
                                border="1px solid #0C1E46"
                                padding="8px 11px"
                                marginRight="20px"
                            >
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                background="#0C1E46"
                                padding="8px 20px"
                                onClick={onSaveBtnClick}
                                isLoading={isLoading}
                                isDisabled={
                                    isLoading ||
                                    companyLogo.error != '' ||
                                    fullLicenseKey.error != '' ||
                                    email.error != '' ||
                                    storeEmail.error != '' ||
                                    bccEmail.error !== '' || 
                                    domain.error!= '' ||
                                    redirectUrlError !== ''
                                }
                            >
                                Save Changes
                            </Button>
                        </>
                        :
                        <Button
                            colorScheme="blue"
                            background="#0C1E46"
                            padding="8px 20px"
                            onClick={onEditToggle}
                        // isDisabled={!fullLicenseKey.value}
                        >
                            Edit
                        </Button>
                    }
                </div>
            </form>
        </Box>
    )
}

export default ManageDealerProfile